import { Info, XCircle } from "lucide-react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "../../components/misc/Card";
import type { UserQuotaMetrics } from "./types";
import { useState } from "react";

interface UserQuotaMetricCardsProps {
    metrics: UserQuotaMetrics;
    isMobile: boolean;
}

const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const infoMessages: Record<keyof UserQuotaMetrics, string> = {
    totalStorage: "The total amount of storage space used by all active users in the system.",
    totalBandwidth: "The total amount of bandwidth consumed by all active users in the current month.",
};

export function UserQuotaMetricCards({ metrics, isMobile }: UserQuotaMetricCardsProps) {
    const [infoMetric, setInfoMetric] = useState<keyof UserQuotaMetrics | null>(null);

    return (
        <>
            <div className={`grid gap-4 grid-cols-${isMobile ? "2" : "2"}`}>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Storage Total
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("totalStorage")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {formatBytes(metrics.totalStorage * 1024 * 1024)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Total storage used by all users
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-semibold">
                            Bandwidth Total
                        </CardTitle>
                        <Info
                            className="h-4 w-4 text-secondary cursor-pointer"
                            onClick={() => setInfoMetric("totalBandwidth")}
                        />
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold">
                            {formatBytes(metrics.totalBandwidth * 1024 * 1024)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Total bandwidth used this month
                        </p>
                    </CardContent>
                </Card>
            </div>

            {infoMetric && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg max-w-md w-full relative">
                        <button
                            className="absolute top-2 right-2"
                            onClick={() => setInfoMetric(null)}
                        >
                            <XCircle className="h-5 w-5 text-red-500" />
                        </button>
                        <h2 className="text-lg font-semibold mb-2 text-primary">
                            {infoMetric
                                .replace(/([A-Z])/g, " $1")
                                .trim()
                                .replace(/^./, (char) => char.toUpperCase())}
                        </h2>
                        <p className="text-sm text-gray-700">
                            {infoMessages[infoMetric]}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}